import { datadogRum } from '@datadog/browser-rum'
import { nanoid } from 'nanoid'
import React, { useEffect, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslate } from 'src/localization/Trns'

const ERROR_ID = 'application.render_crash'
const MAX_COMPONENT_STACK_LENGTH = 4000
const RECOVERY_HEADING_ID = 'application-error-recovery-heading'
const RECOVERY_SURFACE_STYLE: React.CSSProperties = {
  backgroundColor: '#f8fafc',
  boxSizing: 'border-box',
  color: '#172033',
  display: 'grid',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  minHeight: '100vh',
  padding: '24px',
  placeItems: 'center',
}
const RECOVERY_CONTENT_STYLE: React.CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid #d0d7e2',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgb(23 32 51 / 12%)',
  maxWidth: '36rem',
  padding: '32px',
  width: '100%',
}

interface ApplicationErrorBoundaryProps {
  children: React.ReactNode
  createDiagnosticId?: () => string
  reload?: () => void
}

interface RecoveryProps {
  diagnosticId: string
  reload: () => void
}

const Recovery = ({
  diagnosticId,
  reload,
}: RecoveryProps): React.ReactElement => {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const translate = useTranslate()

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <main
      aria-labelledby={RECOVERY_HEADING_ID}
      data-testid="application-error-recovery"
      style={RECOVERY_SURFACE_STYLE}
    >
      <div style={RECOVERY_CONTENT_STYLE}>
        <h1 id={RECOVERY_HEADING_ID} ref={headingRef} tabIndex={-1}>
          {translate('Something went wrong')}
        </h1>
        <p>{translate('Reload the page to try again.')}</p>
        <p>{translate('Diagnostic ID: ${diagnosticId}', { diagnosticId })}</p>
        <button
          data-testid="application-error-reload"
          type="button"
          onClick={reload}
        >
          {translate('Reload page')}
        </button>
      </div>
    </main>
  )
}

export const ApplicationErrorBoundary = ({
  children,
  createDiagnosticId = () => nanoid(10),
  reload = () => window.location.reload(),
}: ApplicationErrorBoundaryProps): React.ReactElement => {
  const diagnosticIdRef = useRef<string | undefined>(undefined)

  const getDiagnosticId = (): string => {
    const diagnosticId = diagnosticIdRef.current ?? createDiagnosticId()
    diagnosticIdRef.current = diagnosticId

    return diagnosticId
  }

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <Recovery diagnosticId={getDiagnosticId()} reload={reload} />
      )}
      onError={(error, info) => {
        datadogRum.addError(error, {
          errorId: ERROR_ID,
          diagnosticId: getDiagnosticId(),
          componentStack:
            info.componentStack?.slice(0, MAX_COMPONENT_STACK_LENGTH) ??
            'unavailable',
        })
      }}
    >
      {children}
    </ErrorBoundary>
  )
}
